import { FunctionComponent, useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import { type Automation, type TriggerConfig, type FlowStep } from '@spike/automation-model';
import { saveAutomationThunk, AutomationsStatus } from '@spike/automations-action';
import { showSuccess, showError } from '@spike/notifications-action';
import { useApiClientWrapper } from 'hooks';
import { ConfirmDialog } from 'components/UI';
import Button from 'components/UI/V3/Button/Button';
import AutomationWizardHeader from './Header/AutomationWizardHeader';
import TriggerSidebar from './Sidebar/TriggerSidebar';
import DelaySidebar, { DelayConfig } from './Sidebar/DelaySidebar';
import SplitSidebar, { SplitConfig } from './Sidebar/SplitSidebar';
import SMSSidebar, { SMSStepConfig } from './SMS/SMSSidebar';
import SMSContentEditor, { SMSContent } from './SMS/SMSContentEditor';
import EmailSidebar, { EmailStepConfig } from './Email/EmailSidebar';
import ToolboxSidebar from './Sidebar/ToolboxSidebar';
import AutomationCanvas from '../Canvas/AutomationCanvas';
import { validateFlow } from '../Canvas/validateFlow';
import { FlowProvider } from './FlowContext';
import {
    useAutomationFlow,
    findStepById,
    DelayStep,
    SplitStep,
    SMSStep,
    EmailStep,
    InsertableType
} from '../Canvas/useAutomationFlow';
import dialogStyles from '../automationDialog.module.scss';
import styles from './AutomationWizard.module.scss';

type WizardStep = 'trigger' | 'flow';

interface AutomationWizardProps {
    onClose: () => void;
    initialName?: string;
    automation?: Automation;
}

const AutomationWizard: FunctionComponent<AutomationWizardProps> = ({ onClose, initialName, automation }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const isEditing = !!automation;

    const initialSteps = useMemo<FlowStep[] | undefined>(() => {
        if (!automation) return undefined;
        // Ensure the trigger step is always present as the first step
        const hasTrigger = automation.steps.some(s => s.type === 'trigger');
        if (hasTrigger) return automation.steps as FlowStep[];
        return [
            {
                id: 'trigger-1',
                type: 'trigger',
                label: 'Trigger',
                subtitle: automation.trigger.kind === 'filter' ? 'Scheduled' : 'Action'
            },
            ...(automation.steps as FlowStep[])
        ];
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const [wizardStep, setWizardStep] = useState<WizardStep>(isEditing ? 'flow' : 'trigger');
    const [saveStatus, setSaveStatus] = useState<AutomationsStatus>(AutomationsStatus.Initial);

    // Sidebar editing state
    const [editingTrigger, setEditingTrigger] = useState(false);
    const [editingDelayId, setEditingDelayId] = useState<string | null>(null);
    const [editingSplitId, setEditingSplitId] = useState<string | null>(null);
    const [editingSmsId, setEditingSmsId] = useState<string | null>(null);
    const [editingEmailId, setEditingEmailId] = useState<string | null>(null);

    // Full-screen SMS content editor
    const [smsContentEditorId, setSmsContentEditorId] = useState<string | null>(null);

    const [showExitPrompt, setShowExitPrompt] = useState(false);

    const [savedTriggerConfig, setSavedTriggerConfig] = useState<TriggerConfig | undefined>(automation?.trigger);

    const [title, setTitle] = useState(
        automation?.name ?? initialName ?? `Automation - ${moment().format('MMM D, YYYY, h:mma')}`
    );
    const {
        steps,
        insertAfterStep,
        insertIntoBranch,
        removeStep,
        updateTriggerStep,
        updateDelayStep,
        flipSplitStep,
        updateSplitStep,
        updateSMSStep,
        updateSMSStepContent,
        updateEmailStep
    } = useAutomationFlow(initialSteps);

    // ── Save ───────────────────────────────────────────────────────────────────

    const handleSave = useCallback(async () => {
        if (!savedTriggerConfig) return;

        const automationToSave: Automation = {
            id: automation?.id ?? '',
            name: title,
            status: automation?.status ?? 'draft',
            trigger: savedTriggerConfig,
            steps,
            createdAt: automation?.createdAt ?? new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };

        const isUpdate = !!automationToSave.id;
        setSaveStatus(AutomationsStatus.Saving);
        try {
            await (dispatch as any)(saveAutomationThunk(apiClientWrapper, automationToSave));
            setSaveStatus(AutomationsStatus.SaveSuccess);
            dispatch(showSuccess(isUpdate ? 'Automation updated successfully.' : 'Automation saved successfully.'));
            onClose();
        } catch {
            setSaveStatus(AutomationsStatus.Error);
            dispatch(showError(isUpdate ? 'Error updating automation.' : 'Error saving automation.'));
        }
    }, [savedTriggerConfig, title, steps, automation, dispatch, apiClientWrapper, onClose]);

    // ── Trigger ────────────────────────────────────────────────────────────────

    const handleTriggerConfigChange = useCallback(
        (subtitle: string, chips?: string[]) => updateTriggerStep(subtitle, chips),
        [updateTriggerStep]
    );

    const handleTriggerSave = useCallback((config: TriggerConfig) => {
        setSavedTriggerConfig(config);
        setEditingTrigger(false);
        setWizardStep('flow');
    }, []);

    // ── Insert ─────────────────────────────────────────────────────────────────

    const handleInsert = useCallback(
        (afterId: string, type: InsertableType) => {
            const newId = insertAfterStep(afterId, type);
            if (!newId) return;
            if (type === 'delay') setEditingDelayId(newId);
            if (type === 'split') setEditingSplitId(newId);
            if (type === 'sms') setEditingSmsId(newId);
            if (type === 'email') setEditingEmailId(newId);
        },
        [insertAfterStep]
    );

    // ── Delay ──────────────────────────────────────────────────────────────────

    const handleDelayConfigChange = useCallback(
        (config: DelayConfig) => {
            if (!editingDelayId) return;
            updateDelayStep(editingDelayId, config.amount, config.unit, config.extraMinutes);
        },
        [editingDelayId, updateDelayStep]
    );

    const handleDelaySave = useCallback(
        (config: DelayConfig) => {
            if (!editingDelayId) return;
            updateDelayStep(editingDelayId, config.amount, config.unit, config.extraMinutes);
            setEditingDelayId(null);
        },
        [editingDelayId, updateDelayStep]
    );

    const editingDelayStep = editingDelayId
        ? (findStepById(editingDelayId, steps) as DelayStep | undefined)
        : undefined;

    // ── Split ──────────────────────────────────────────────────────────────────

    const handleSplitConfigChange = useCallback(
        (config: SplitConfig) => {
            if (!editingSplitId) return;
            updateSplitStep(editingSplitId, config.conditions);
        },
        [editingSplitId, updateSplitStep]
    );

    const handleSplitSave = useCallback(
        (config: SplitConfig) => {
            if (!editingSplitId) return;
            updateSplitStep(editingSplitId, config.conditions);
            setEditingSplitId(null);
        },
        [editingSplitId, updateSplitStep]
    );

    const editingSplitStep = editingSplitId
        ? (findStepById(editingSplitId, steps) as SplitStep | undefined)
        : undefined;

    // ── SMS sidebar ────────────────────────────────────────────────────────────

    const handleSmsConfigChange = useCallback(
        (config: SMSStepConfig) => {
            if (!editingSmsId) return;
            updateSMSStep(editingSmsId, config.name, config.content, config.skipCampaign, config.skipFlow);
        },
        [editingSmsId, updateSMSStep]
    );

    const handleSmsSave = useCallback(
        (config: SMSStepConfig) => {
            if (!editingSmsId) return;
            updateSMSStep(editingSmsId, config.name, config.content, config.skipCampaign, config.skipFlow);
            setEditingSmsId(null);
        },
        [editingSmsId, updateSMSStep]
    );

    const editingSmsStep = editingSmsId ? (findStepById(editingSmsId, steps) as SMSStep | undefined) : undefined;

    // ── Email sidebar ──────────────────────────────────────────────────────────

    const handleEmailConfigChange = useCallback(
        (config: EmailStepConfig) => {
            if (!editingEmailId) return;
            updateEmailStep(editingEmailId, config);
        },
        [editingEmailId, updateEmailStep]
    );

    const handleEmailSave = useCallback(
        (config: EmailStepConfig) => {
            if (!editingEmailId) return;
            updateEmailStep(editingEmailId, config);
            setEditingEmailId(null);
        },
        [editingEmailId, updateEmailStep]
    );

    const editingEmailStep = editingEmailId
        ? (findStepById(editingEmailId, steps) as EmailStep | undefined)
        : undefined;

    // ── SMS content editor (full-screen) ───────────────────────────────────────

    const smsContentEditorStep = smsContentEditorId
        ? (findStepById(smsContentEditorId, steps) as SMSStep | undefined)
        : undefined;

    const handleSmsContentSave = useCallback(
        (content: SMSContent) => {
            if (!smsContentEditorId) return;
            updateSMSStepContent(smsContentEditorId, content);
            setSmsContentEditorId(null);
        },
        [smsContentEditorId, updateSMSStepContent]
    );

    // ── Sidebar resolution ─────────────────────────────────────────────────────

    const showTriggerSidebar = wizardStep === 'trigger' || editingTrigger;
    const showDelaySidebar = !showTriggerSidebar && !!editingDelayId;
    const showSplitSidebar = !showTriggerSidebar && !showDelaySidebar && !!editingSplitId;
    const showSmsSidebar = !showTriggerSidebar && !showDelaySidebar && !showSplitSidebar && !!editingSmsId;
    const showEmailSidebar =
        !showTriggerSidebar && !showDelaySidebar && !showSplitSidebar && !showSmsSidebar && !!editingEmailId;

    const isSaving = saveStatus === AutomationsStatus.Saving;

    // A filter trigger carries no record, and the empty object is what says so.
    const mergeTagTrigger = useMemo(() => {
        if (savedTriggerConfig?.kind === 'action') return { event: savedTriggerConfig.eventType };
        if (savedTriggerConfig?.kind === 'schedule') return { anchor: savedTriggerConfig.anchor };

        return {};
    }, [savedTriggerConfig]);

    // Inline arrows here gave every render a new identity, which invalidated the
    // canvas node memo and made ReactFlow rebuild the whole tree on each keystroke.
    const inFlow = wizardStep === 'flow';
    const openTrigger = useCallback(() => setEditingTrigger(true), []);
    const canvasHandlers = useMemo(
        () => ({
            trigger: inFlow ? openTrigger : undefined,
            delay: inFlow ? setEditingDelayId : undefined,
            split: inFlow ? setEditingSplitId : undefined,
            sms: inFlow ? setEditingSmsId : undefined,
            email: inFlow ? setEditingEmailId : undefined
        }),
        [inFlow, openTrigger]
    );

    // Deleting a step leaves its panel pointing at something that no longer
    // exists, so the panel has to close with it.
    const handleRemoveStep = useCallback(
        (id: string) => {
            setEditingDelayId(prev => (prev === id ? null : prev));
            setEditingSplitId(prev => (prev === id ? null : prev));
            setEditingSmsId(prev => (prev === id ? null : prev));
            setEditingEmailId(prev => (prev === id ? null : prev));
            setSmsContentEditorId(prev => (prev === id ? null : prev));
            removeStep(id);
        },
        [removeStep]
    );

    // Saving mid-edit would persist whatever the panel has typed so far, so the
    // open panel has to be closed before the automation as a whole can be saved.
    const openPanel = showTriggerSidebar
        ? 'the trigger'
        : showDelaySidebar
        ? 'the delay step'
        : showSplitSidebar
        ? 'the split'
        : showSmsSidebar
        ? 'the SMS step'
        : showEmailSidebar
        ? 'the email step'
        : smsContentEditorId
        ? 'the SMS content'
        : null;

    const saveBlockers = useMemo(() => {
        if (wizardStep !== 'flow') return ['Finish setting up the trigger before saving.'];

        const reasons: string[] = [];
        if (openPanel) reasons.push(`You are still editing ${openPanel} — apply or close that panel first.`);
        if (!savedTriggerConfig) reasons.push('The trigger has not been configured yet.');

        return [...reasons, ...validateFlow(steps)];
    }, [wizardStep, openPanel, savedTriggerConfig, steps]);

    const canSave = !isSaving && saveBlockers.length === 0;

    return (
        <FlowProvider>
            <div className={styles.container}>
                <ConfirmDialog
                    open={showExitPrompt}
                    className={dialogStyles.dialog}
                    onCancel={() => setShowExitPrompt(false)}
                    title="Exit Without Saving?"
                    question="Your unsaved changes will be lost."
                    actions={
                        <>
                            <Button
                                fullWidth
                                size="large"
                                variant="danger"
                                label="Discard"
                                style={{ minWidth: 'unset' }}
                                onClick={onClose}
                            />
                            <Button
                                fullWidth
                                size="large"
                                variant="outlined"
                                label="Keep Editing"
                                style={{ margin: 0, minWidth: 'unset' }}
                                onClick={() => setShowExitPrompt(false)}
                            />
                        </>
                    }
                />

                {/* Full-screen SMS content editor — renders over everything */}
                {smsContentEditorId && smsContentEditorStep && (
                    <SMSContentEditor
                        initialContent={smsContentEditorStep.content}
                        trigger={mergeTagTrigger}
                        onSave={handleSmsContentSave}
                        onClose={() => setSmsContentEditorId(null)}
                    />
                )}

                <AutomationWizardHeader
                    title={title}
                    onClose={() => setShowExitPrompt(true)}
                    onTitleChange={setTitle}
                    onSave={canSave ? handleSave : undefined}
                    saveBlockers={saveBlockers}
                    status={automation?.status}
                    isSaving={isSaving}
                />
                <div className={styles.body}>
                    {showTriggerSidebar ? (
                        <TriggerSidebar
                            initialConfig={savedTriggerConfig}
                            onSave={handleTriggerSave}
                            onCancel={editingTrigger ? () => setEditingTrigger(false) : undefined}
                            onConfigChange={handleTriggerConfigChange}
                        />
                    ) : showDelaySidebar ? (
                        <DelaySidebar
                            initialConfig={
                                editingDelayStep
                                    ? {
                                          amount: editingDelayStep.amount,
                                          unit: editingDelayStep.unit,
                                          extraMinutes: editingDelayStep.extraMinutes
                                      }
                                    : undefined
                            }
                            onSave={handleDelaySave}
                            onCancel={() => setEditingDelayId(null)}
                            onConfigChange={handleDelayConfigChange}
                        />
                    ) : showSplitSidebar ? (
                        <SplitSidebar
                            initialConfig={
                                editingSplitStep ? { conditions: editingSplitStep.conditions ?? [] } : undefined
                            }
                            onSave={handleSplitSave}
                            onCancel={() => setEditingSplitId(null)}
                            onConfigChange={handleSplitConfigChange}
                        />
                    ) : showSmsSidebar ? (
                        <SMSSidebar
                            key={editingSmsId}
                            initialConfig={
                                editingSmsStep
                                    ? {
                                          name: editingSmsStep.smsName,
                                          content: editingSmsStep.content,
                                          skipCampaign: editingSmsStep.skipCampaign,
                                          skipFlow: editingSmsStep.skipFlow
                                      }
                                    : undefined
                            }
                            onSave={handleSmsSave}
                            onCancel={() => setEditingSmsId(null)}
                            onConfigChange={handleSmsConfigChange}
                            onEditContent={() => {
                                if (editingSmsId) setSmsContentEditorId(editingSmsId);
                            }}
                        />
                    ) : showEmailSidebar ? (
                        <EmailSidebar
                            key={editingEmailId}
                            initialConfig={
                                editingEmailStep
                                    ? {
                                          name: editingEmailStep.emailName,
                                          emailTemplateId: editingEmailStep.emailTemplateId ?? null,
                                          subjectLine: editingEmailStep.subjectLine,
                                          previewText: editingEmailStep.previewText,
                                          senderName: editingEmailStep.senderName,
                                          senderEmail: editingEmailStep.senderEmail,
                                          useAsReplyTo: editingEmailStep.useAsReplyTo,
                                          skipRecentlyEmailed: editingEmailStep.skipRecentlyEmailed
                                      }
                                    : undefined
                            }
                            onSave={handleEmailSave}
                            onCancel={() => setEditingEmailId(null)}
                            onConfigChange={handleEmailConfigChange}
                        />
                    ) : (
                        <ToolboxSidebar />
                    )}
                    <AutomationCanvas
                        steps={steps}
                        onInsert={handleInsert}
                        onInsertBranch={insertIntoBranch}
                        onRemove={handleRemoveStep}
                        onFlipSplit={flipSplitStep}
                        onTriggerClick={canvasHandlers.trigger}
                        onDelayClick={canvasHandlers.delay}
                        onSplitClick={canvasHandlers.split}
                        onSmsClick={canvasHandlers.sms}
                        onEmailClick={canvasHandlers.email}
                    />
                </div>
            </div>
        </FlowProvider>
    );
};

export default AutomationWizard;
